import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { ImpactService } from 'src/app/services/impact.service';
import * as fromBia from './bia.selectors';

import * as BiaActions from './bia.actions';
import { select, Store } from '@ngrx/store';
import { Bia } from 'src/app/core/models/bia.model';
import { loadVitalRecord } from './bia.actions';
import { updateStatusReset } from '../status/status.actions';
import { StatusType } from 'src/app/core/enum/status.enum';
import { Transformation } from 'src/@utility/model-transformation';

@Injectable()
export class BiaEffects {
  loadAllBias$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadAllBias),
      switchMap(() => {
        return this.impactService
          .getBias({ 'isEnabled.equals': true, size: 500000, page: 0 })
          .pipe(
            map((response: any) => {
              return BiaActions.loadAllBiasSuccess({
                bias: response.body,
              });
            })
          );
      })
    );
  });

  loadBias$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadBias),
      withLatestFrom(this.store.select(fromBia.getBias)),
      switchMap(([action, bias]) => {
        return this.impactService.getBias(action.options).pipe(
          map((response: any) => {
            return BiaActions.loadBiasSuccess({
              bias: response.body.map((bia) => ({
                ...bia,
                name: bia.location
                  ? `${bia.name} @ ${bia.location.name}`
                  : bia.name,
              })),
            });
          }),
          catchError((error: Error) => {
            return of(BiaActions.loadBiasFailure({ error }));
          })
        );
      })
    );
  });

  loadCallTrees$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadCallTree),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(
              this.store.select(fromBia.getCallTrees, { id: action.id })
            )
          ),
        (action, data) => data
      ),
      switchMap(([action, callTree]) => {
        if (!callTree) {
          return this.impactService.getCallTrees(action.id).pipe(
            map((response) => {
              return BiaActions.loadCallTreeSuccess({
                id: action.id,
                callTreeList: response,
              });
            })
          );
        }
        return of(BiaActions.dummyAction());
      })
    );
  });

  loadCallTreeMultiple$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadCallTreeMultiple),
      switchMap((action) => {
        return this.impactService.getCallTreeMultiple(action.ids).pipe(
          mergeMap((response) => {
            return action.ids.map((id) =>
              BiaActions.loadCallTreeSuccess({
                id,
                callTreeList: response.filter((res) => res.bia?.id === id),
              })
            );
          })
        );
      })
    );
  });

  updateCallTrees$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.adjustAppCallTree),
      switchMap((action) => {
        return this.impactService
          .adjustAppCallTree(action.appCallTreeList)
          .pipe(
            map((appCallTreeList) =>
              BiaActions.updateAppCallTreeSuccess({
                ...action,
                appCallTreeList,
              })
            )
          );
      })
    );
  });

  loadProcesses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadProcesses),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(
              this.store.select(fromBia.getProcesses, { id: action.id })
            )
          ),
        (action, data) => data
      ),
      switchMap(([action, processes]) => {
        if (!processes) {
          return this.impactService.getProcesses(action.id).pipe(
            map((response) => {
              return BiaActions.loadProcessesSuccess({
                id: action.id,
                processes: response.body,
              });
            })
          );
        }
        return of(BiaActions.dummyAction());
      })
    );
  });

  addProcess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.addProcess),
      switchMap((action) => {
        if (!action.process) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService.addProcess(action.process).pipe(
          map((process) => {
            if (!process) {
              return BiaActions.dummyAction();
            }

            return BiaActions.addProcessSuccess({
              id: action.id,
              process,
            });
          })
        );
      })
    );
  });

  updateProcess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.updateProcess),
      switchMap((action) => {
        const { process } = action;
        if (!process) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService.updateProcess(process.id, process).pipe(
          map((process) => {
            if (!process) {
              return BiaActions.dummyAction();
            }

            return BiaActions.updateProcessSuccess({
              id: action.id,
              process,
            });
          })
        );
      })
    );
  });

  deleteProcess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.deleteProcess),
      switchMap((action) => {
        const { processId } = action;
        if (!processId) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService.softDeleteProcess(processId).pipe(
          map(() => {
            updateStatusReset(StatusType.Deleted, this.store);
            return BiaActions.deleteProcessSuccess({
              id: action.id,
              processId,
            });
          }),
          catchError(() =>
            of(updateStatusReset(StatusType.Failure, this.store))
          )
        );
      })
    );
  });

  loadImpacts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadImpact),
      switchMap((action) => {
        return this.impactService.getImpacts(action.id).pipe(
          map((impacts) => {
            return BiaActions.loadImpactSuccess({
              id: action.id,
              impact: impacts,
            });
          })
        );
      })
    );
  });
  addOrUpdateImpact$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.addOrUpdateImpact),
      switchMap((action) => {
        let serviceToCall: Observable<any>;
        if (action.impact.id) {
          serviceToCall = this.impactService.updateImpact(
            action.impact.id,
            action.impact
          );
        } else {
          serviceToCall = this.impactService.addImpact(action.impact);
        }
        return serviceToCall.pipe(
          map((response) => {
            return BiaActions.addOrUpdateImpactSuccess({
              id: action.id,
              impact: response,
            });
          })
        );
      })
    );
  });

  loadInDependencies$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadInDependencies),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(
              this.store.select(fromBia.getInDependencies, { id: action.id })
            )
          ),
        (action, data) => data
      ),
      switchMap(([action, inDependencies]) => {
        if (!inDependencies) {
          return this.impactService.getInDependencies(action.id).pipe(
            map((dependencies) => {
              return BiaActions.loadInDependenciesSuccess({
                id: action.id,
                inDependencies: dependencies,
              });
            })
          );
        }
        return of(BiaActions.dummyAction());
      })
    );
  });

  addInDependency$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.addInDependency),
      switchMap((action) => {
        if (!action.inDependency) {
          return of(BiaActions.dummyAction());
        }

        const { dependencyRto } = action.inDependency;
        delete action.inDependency.dependencyRto;

        return this.impactService.addInDependency(action.inDependency).pipe(
          map((inDependency) => {
            if (!inDependency) {
              return BiaActions.dummyAction();
            }

            updateStatusReset(StatusType.Created, this.store);
            return BiaActions.addInDependencySuccess({
              id: action.id,
              inDependency: { ...inDependency, dependencyRto },
            });
          })
        );
      })
    );
  });

  updateInDependency$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.updateInDependency),
      switchMap((action) => {
        const { inDependency } = action;
        if (!inDependency) {
          return of(BiaActions.dummyAction());
        }

        const { dependencyRto, rto } = inDependency;
        delete inDependency.dependencyRto;

        return this.impactService
          .updateInDependency(inDependency.id, inDependency)
          .pipe(
            map((inDependency) => {
              if (!inDependency) {
                return BiaActions.dummyAction();
              }

              updateStatusReset(StatusType.Updated, this.store);
              return BiaActions.updateInDependencySuccess({
                id: action.id,
                inDependency: { ...inDependency, dependencyRto, rto },
              });
            })
          );
      })
    );
  });

  deleteInDependency$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.deleteInDependency),
      switchMap((action) => {
        const { inDependencyId } = action;
        if (!inDependencyId) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService.deleteInDependency(inDependencyId).pipe(
          map(() =>
            BiaActions.deleteInDependencySuccess({
              id: action.id,
              inDependencyId,
            })
          )
        );
      })
    );
  });

  loadExDependencies$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadExDependencies),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(
              this.store.select(fromBia.getExDependencies, { id: action.id })
            )
          ),
        (action, data) => data
      ),
      switchMap(([action, exDependencies]) => {
        if (!exDependencies || action.options.dropCache) {
          return this.impactService.getExDependencies(action.id).pipe(
            map((response) => {
              return BiaActions.loadExDependenciesSuccess({
                id: action.id,
                exDependencies: response.body,
              });
            })
          );
        }
        return of(BiaActions.dummyAction());
      })
    );
  });

  addExDependency$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.addExDependency),
      switchMap((action) => {
        if (!action.exDependency) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService.addExDependency(action.exDependency).pipe(
          switchMap((exDependency) =>
            this.impactService.getExDependency(exDependency.id)
          ),
          map((exDependency) => {
            if (!exDependency) {
              return BiaActions.dummyAction();
            }

            return BiaActions.addExDependencySuccess({
              id: action.id,
              exDependency,
            });
          })
        );
      })
    );
  });

  updateExDependency$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.updateExDependency),
      switchMap((action) => {
        const { exDependency } = action;
        if (!exDependency) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService
          .updateExDependency(exDependency.id, exDependency)
          .pipe(
            switchMap((_) =>
              this.impactService.getExDependency(exDependency.id)
            ),
            map((exDependency) => {
              if (!exDependency) {
                return BiaActions.dummyAction();
              }

              return BiaActions.updateExDependencySuccess({
                id: action.id,
                exDependency,
              });
            })
          );
      })
    );
  });

  deleteExDependency$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.deleteExDependency),
      switchMap((action) => {
        const { exDependencyId } = action;
        if (!exDependencyId) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService.deleteExDependency(exDependencyId).pipe(
          map(() =>
            BiaActions.deleteExDependencySuccess({
              id: action.id,
              exDependencyId,
            })
          )
        );
      })
    );
  });

  loadPeakTime$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadPeakTime),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(
              this.store.select(fromBia.getBiaPeakTimes, { id: action.id })
            )
          ),
        (action, data) => data
      ),
      switchMap(([action, peakTimes]) => {
        if (!peakTimes) {
          return this.impactService.getBiaPeakTime(action.id).pipe(
            map((response) => {
              return BiaActions.loadPeakTimeSuccess({
                id: action.id,
                peakTimes: response.body,
              });
            })
          );
        }
        return of(BiaActions.dummyAction());
      })
    );
  });

  addPeakTime$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.addPeakTime),
      switchMap((action) => {
        if (!action.peakTime) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService.addPeakTime(action.peakTime).pipe(
          map((peakTime) => {
            if (!peakTime) {
              return BiaActions.dummyAction();
            }

            return BiaActions.addPeakTimeSuccess({
              id: action.id,
              peakTime,
            });
          })
        );
      })
    );
  });

  loadDeadLine$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadCriticalDeadline),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(
              this.store.select(fromBia.getBiaDeadLines, { id: action.id })
            )
          ),
        (action, data) => data
      ),
      switchMap(([action, deadLines]) => {
        if (!deadLines) {
          return this.impactService.getBiaDeadLine(action.id).pipe(
            map((response) => {
              return BiaActions.loadCriticalDeadlineSuccess({
                id: action.id,
                cds: response.body,
              });
            })
          );
        }
        return of(BiaActions.dummyAction());
      })
    );
  });

  addDeadLine$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.addCriticalDeadline),
      switchMap((action) => {
        if (!action.cd) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService.addDeadLine(action.cd).pipe(
          map((deadLine) => {
            if (!deadLine) {
              return BiaActions.dummyAction();
            }

            return BiaActions.addCriticalDeadlineSuccess({
              id: action.id,
              cd: deadLine,
            });
          })
        );
      })
    );
  });

  loadAdInfo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadAdInfo),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(
              this.store.select(fromBia.getAdInfo, { id: action.id })
            )
          ),
        (action, data) => data
      ),
      switchMap(([action, adInfo]) => {
        if (!adInfo) {
          return this.impactService.getAdditionalInformation(action.id).pipe(
            map((response) => {
              return BiaActions.loadAdInfoSuccess({
                id: action.id,
                adInfoes: response.body,
              });
            })
          );
        }
        return of(BiaActions.dummyAction());
      })
    );
  });

  addOrUpdateAdInfo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.addOrUpdateAdInfo),
      switchMap((action) => {
        let serviceToCall: Observable<any>;
        const adInfo = action.adInfo;
        const isFileUpload = adInfo.isFileUpload;
        const isFileDelete = adInfo.isFileDelete || false;
        delete adInfo.isFileUpload;
        delete adInfo.isFileDelete;
        if (action.adInfo.id) {
          serviceToCall = this.impactService.updateAdditionalInformation(
            action.adInfo.id,
            adInfo
          );
        } else {
          serviceToCall = this.impactService.addAdditionalInformation(adInfo);
        }
        return serviceToCall.pipe(
          map((response) => {
            if (isFileUpload) {
              updateStatusReset(StatusType.Uploaded, this.store);
            }
            if (isFileDelete) {
              updateStatusReset(StatusType.fileDeleted, this.store);
            }
            return BiaActions.addOrUpdateAdInfoSuccess({
              id: action.id,
              adInfo: response,
            });
          })
        );
      })
    );
  });

  loadVitalRecord$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadVitalRecord),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(
              this.store.select(fromBia.getVitalRecord, { id: action.id })
            )
          ),
        (action, data) => data
      ),
      switchMap(([action, vitalRecord]) => {
        //if (!vitalRecord) {
        return this.impactService.getVitalRecord(action.id).pipe(
          map((response) => {
            const users = response.body.users
              ? response.body.users.map(Transformation.user)
              : [];
            return BiaActions.loadVitalRecordSuccess({
              id: action.id,
              vitalRecords: response.body.biaVitalRecordDtos.map((vital) => ({
                ...vital,
                recordType: vital.recordType,
                custodian: vital.recordCustodian || ' ',
              })),
            });
          })
        );
        // }
        // return of(BiaActions.dummyAction());
      })
    );
  });

  addVitalRecord$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.addVitalRecord),
      switchMap((action) => {
        if (!action.vitalRecord) return of(BiaActions.dummyAction());
        return this.impactService.addVitalRecord(action.vitalRecord).pipe(
          map((vt) => {
            this.store.dispatch(loadVitalRecord({ id: action.id }));
            return BiaActions.dummyAction();
          })
        );
      })
    );
  });

  updateVitalRecord$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.updateVitalRecord),
      switchMap((action) => {
        if (!action.vitalRecord) return of(BiaActions.dummyAction());
        return this.impactService
          .updateVitalRecord(action.vitalRecord.id, action.vitalRecord)
          .pipe(
            map((vitalRecord) => {
              this.store.dispatch(loadVitalRecord({ id: action.id }));
              return BiaActions.dummyAction();
            })
          );
      })
    );
  });

  loadItInfra$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadITInfra),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(
              this.store.select(fromBia.getItInfra, { id: action.id })
            )
          ),
        (action, data) => data
      ),
      switchMap(([action, itInfra]) => {
        if (!itInfra) {
          return this.impactService.getItInfra(action.id).pipe(
            map((response) => {
              return BiaActions.loadITInfraSuccess({
                id: action.id,
                iTInfras: response.body,
              });
            })
          );
        }
        return of(BiaActions.dummyAction());
      })
    );
  });

  addItInfra$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.addITInfra),
      switchMap((action) => {
        if (!action.iTInfra) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService.addItInfra(action.iTInfra).pipe(
          map((itInf) => {
            if (!itInf) {
              return BiaActions.dummyAction();
            }

            return BiaActions.addITInfraSuccess({
              id: action.id,
              iTInfra: itInf,
            });
          })
        );
      })
    );
  });

  updateItInfra$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.updateITInfra),
      switchMap((action) => {
        if (!action.iTInfra) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService
          .updateItInfra(action.iTInfra.id, action.iTInfra)
          .pipe(
            map((itFra) => {
              if (!itFra) {
                return BiaActions.dummyAction();
              }

              return BiaActions.updateITInfraSuccess({
                id: action.id,
                iTInfra: itFra,
              });
            })
          );
      })
    );
  });

  loadSplReq$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadSplReq),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(
              this.store.select(fromBia.getSpleReq, { id: action.id })
            )
          ),
        (action, data) => data
      ),
      switchMap(([action, splReq]) => {
        if (!splReq) {
          return this.impactService.getSplReq(action.id).pipe(
            map((response) => {
              return BiaActions.loadSplReqSuccess({
                id: action.id,
                splReqs: response.body,
              });
            })
          );
        }
        return of(BiaActions.dummyAction());
      })
    );
  });

  addSplReq$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.addSplReq),
      switchMap((action) => {
        if (!action.splReq) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService.addSplReq(action.splReq).pipe(
          map((splRe) => {
            if (!splRe) {
              return BiaActions.dummyAction();
            }

            return BiaActions.addSplReqSuccess({
              id: action.id,
              splReq: splRe,
            });
          })
        );
      })
    );
  });

  updateSplReq$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.updateSplReq),
      switchMap((action) => {
        if (!action.splReq) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService
          .updateSplReq(action.splReq.id, action.splReq)
          .pipe(
            map((splR) => {
              if (!splR) {
                return BiaActions.dummyAction();
              }

              return BiaActions.updateSplReqSuccess({
                id: action.id,
                splReq: splR,
              });
            })
          );
      })
    );
  });

  addAppNSoft$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.addAppNSoft),
      switchMap((action) => {
        if (!action.appNSoft) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService.addAppNSoft(action.appNSoft).pipe(
          map((apns) => {
            if (!apns) {
              return BiaActions.dummyAction();
            }
            updateStatusReset(StatusType.Created, this.store);
            return BiaActions.addAppNSoftSuccess({
              id: action.id,
              appNSoft: apns,
            });
          })
        );
      })
    );
  });

  loadAppNSoft$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadAppNSoft),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(
              this.store.select(fromBia.getAppNSoft, { id: action.id })
            )
          ),
        (action, data) => data
      ),
      switchMap(([action, appNs]) => {
        if (!appNs) {
          return this.impactService.getAppNSoft(action.id).pipe(
            map((response) => {
              return BiaActions.loadAppNSoftSuccess({
                id: action.id,
                appNSofts: response.body,
              });
            })
          );
        }
        return of(BiaActions.dummyAction());
      })
    );
  });
  updateAppsNSoft$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.updateAppNSoft),
      switchMap((action) => {
        if (!action.appNSoft) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService
          .updateAppsNSoft(action.appNSoft.id, action.appNSoft)
          .pipe(
            map((apns) => {
              if (!apns) {
                return BiaActions.dummyAction();
              }

              updateStatusReset(StatusType.Updated, this.store);
              return BiaActions.updateAppNSoftSuccess({
                id: action.id,
                appNSoft: apns,
              });
            })
          );
      })
    );
  });

  deleteAppsNSoft$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.deleteAppNSoft),
      switchMap((action) => {
        if (!action.appNSoftId) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService.deleteAppNSoft(action.appNSoftId).pipe(
          map((sr) => {
            updateStatusReset(StatusType.Deleted, this.store);
            return BiaActions.deleteAppNSoftSuccess(action);
          })
        );
      })
    );
  });

  loadStaffReq$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadStaffReq),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(
              this.store.select(fromBia.getStaffReq, { id: action.id })
            )
          ),
        (action, data) => data
      ),
      switchMap(([action, staff]) => {
        if (!staff) {
          return this.impactService.getStaffReq(action.id).pipe(
            map((response) => {
              return BiaActions.loadStaffReqSuccess({
                id: action.id,
                staffRequirements: response.body,
              });
            })
          );
        }
        return of(BiaActions.dummyAction());
      })
    );
  });

  addStaffReq$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.addStaffReq),
      switchMap((action) => {
        if (!action.staffRequirement) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService.addStaffReq(action.staffRequirement).pipe(
          map((sr) => {
            if (!sr) {
              return BiaActions.dummyAction();
            }

            updateStatusReset(StatusType.Created, this.store);
            return BiaActions.addStaffReqSuccess({
              id: action.id,
              staffRequirement: sr,
            });
          })
        );
      })
    );
  });

  updateStaffReq$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.updateStaffReq),
      switchMap((action) => {
        if (!action.staffRequirement) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService
          .updateStaffReq(action.staffRequirement.id, action.staffRequirement)
          .pipe(
            map((sr) => {
              if (!sr) {
                return BiaActions.dummyAction();
              }

              updateStatusReset(StatusType.Uploaded, this.store);
              return BiaActions.updateStaffReqSuccess({
                id: action.id,
                staffRequirement: sr,
              });
            })
          );
      })
    );
  });

  deleteStaffReq$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.deleteStaffReq),
      switchMap((action) => {
        if (!action.staffReqId) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService.deleteStaffReq(action.staffReqId).pipe(
          map((sr) => {
            updateStatusReset(StatusType.Deleted, this.store);
            return BiaActions.deleteStaffReqSuccess(action);
          })
        );
      })
    );
  });

  loadSpof$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadSpof),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(
              this.store.select(fromBia.getSpof, { id: action.id })
            )
          ),
        (action, data) => data
      ),
      switchMap(([action, sp]) => {
        if (!sp) {
          return this.impactService.getSpof(action.id).pipe(
            map((response) => {
              return BiaActions.loadSpofSuccess({
                id: action.id,
                spof: response.body,
              });
            })
          );
        }
        return of(BiaActions.dummyAction());
      })
    );
  });

  addOrUpdateSpof$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.addOrUpdateSpof),
      switchMap((action) => {
        let serviceToCall: Observable<any>;
        if (action.spof.id) {
          serviceToCall = this.impactService.updateSpof(
            action.spof.id,
            action.spof
          );
        } else {
          serviceToCall = this.impactService.addSpof(action.spof);
        }
        return serviceToCall.pipe(
          map((response) => {
            // TODO response update from api is null
            return BiaActions.addOrUpdateSpofSuccess({
              id: action.id,
              spof: response,
            });
          })
        );
      })
    );
  });

  // Resources --> HR start

  loadHR$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadHR),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(this.store.select(fromBia.getHR, { id: action.id }))
          ),
        (action, data) => data
      ),
      switchMap(([action, hrs]) => {
        if (!hrs) {
          return this.impactService.getHR(action.id).pipe(
            map((response) => {
              return BiaActions.loadHRSuccess({
                id: action.id,
                hrs: response.body,
              });
            })
          );
        }
        return of(BiaActions.dummyAction());
      })
    );
  });

  addHR$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.addHR),
      switchMap((action) => {
        if (!action.hr) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService.addHR(action.hr).pipe(
          map((hrs) => {
            if (!hrs) {
              return BiaActions.dummyAction();
            }
            updateStatusReset(StatusType.Created, this.store);
            return BiaActions.addHRSuccess({
              id: action.id,
              hr: hrs,
            });
          })
        );
      })
    );
  });

  updateHR$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.updateHR),
      switchMap((action) => {
        if (!action.hr) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService.updateHR(action.hr.id, action.hr).pipe(
          map((hrs) => {
            if (!hrs) {
              return BiaActions.dummyAction();
            }

            updateStatusReset(StatusType.Updated, this.store);
            return BiaActions.updateHRSuccess({
              id: action.id,
              hr: hrs,
            });
          })
        );
      })
    );
  });

  deleteHR$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.deleteHR),
      switchMap((action) => {
        if (!action.id) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService.deleteHR(action.id).pipe(
          map((hr) => {
            updateStatusReset(StatusType.Deleted, this.store);
            return BiaActions.deleteHRSuccess({
              id: action.id,
              hr: hr.id,
            });
          })
        );
      })
    );
  });

  // Resources --> HR END

  // Resources --> Call Tree start

  loadResourceCallTree$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadResourceCallTree),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(
              this.store.select(fromBia.getResourceCallTree, { id: action.id })
            )
          ),
        (action, data) => data
      ),
      switchMap(([action, resourceCallTrees]) => {
        // if (!resourceCallTrees) {
        return this.impactService.getResourceCallTree(action.id).pipe(
          map((response) => {
            return BiaActions.loadResourceCallTreeSuccess({
              id: action.id,
              resourceCallTree: response.body,
            });
          })
        );
        // }
        // return of(BiaActions.dummyAction());
      })
    );
  });

  addResourceCallTree$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.addResourceCallTree),
      switchMap((action) => {
        if (!action.resourceCallTree) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService
          .addResourceCallTree(action.resourceCallTree)
          .pipe(
            map((resourceCallTree) => {
              if (!resourceCallTree) {
                return BiaActions.dummyAction();
              }
              updateStatusReset(StatusType.Created, this.store);
              return BiaActions.addResourceCallTreeSuccess({
                id: action.id,
                resourceCallTree,
              });
            })
          );
      })
    );
  });

  updateResourceCallTree$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.updateResourceCallTree),
      switchMap((action) => {
        if (!action.resourceCallTree) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService
          .updateResourceCallTree(
            action.resourceCallTree.id,
            action.resourceCallTree
          )
          .pipe(
            map((resourceCallTree) => {
              if (!resourceCallTree) {
                return BiaActions.dummyAction();
              }

              updateStatusReset(StatusType.Updated, this.store);
              return BiaActions.updateResourceCallTreeSuccess({
                id: action.id,
                resourceCallTree,
              });
            })
          );
      })
    );
  });

  deleteResourceCallTree$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.deleteResourceCallTree),
      switchMap((action) => {
        if (!action.resourceCallTree) {
          return of(BiaActions.dummyAction());
        }

        return this.impactService
          .deleteResourceCallTree(action.resourceCallTree)
          .pipe(
            map((hr) => {
              updateStatusReset(StatusType.Deleted, this.store);
              return BiaActions.deleteResourceCallTree({
                id: action.id,
                resourceCallTree: hr.deleteResourceCallTree,
              });
            })
          );
      })
    );
  });
  // Resources --> Role Call Tree END

  // BiaIntroEffects
  loadBiaIntro$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.loadBiaIntro),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(this.store.select(fromBia.getBiaIntro, {}))
          ),
        (action, data) => data
      ),
      switchMap(([action, biaIntro]) => {
        // if (!biaIntro || biaIntro.length == 0) {
        return this.impactService.getBiaIntro(action.biaId).pipe(
          map((response) => {
            return BiaActions.loadBiaIntroSuccess({
              biaId: action.biaId,
              biaIntro: response.body,
            });
          })
        );
        // }
        // return of(BiaActions.dummyAction());
      })
    );
  });

  addOrUpdateBiaIntro$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BiaActions.addOrUpdateBiaIntro),
      switchMap((action) => {
        let serviceToCall: Observable<any>;
        const biaIntro = action.biaIntro;
        const isFileUpload = biaIntro.isFileUpload;
        const isFileDelete = biaIntro.isFileDelete || false;
        delete biaIntro.isFileUpload;
        delete biaIntro.isFileDelete;
        if (action.id) {
          serviceToCall = this.impactService.updateBiaIntro(
            action.id,
            biaIntro
          );
        } else {
          serviceToCall = this.impactService.addBiaIntro(biaIntro);
        }
        return serviceToCall.pipe(
          map((response) => {
            if (isFileUpload)
              updateStatusReset(StatusType.Uploaded, this.store);
            if (isFileDelete)
              updateStatusReset(StatusType.fileDeleted, this.store);
            return BiaActions.addOrUpdateBiaIntroSuccess({
              id: response.id,
              biaIntro: response,
            });
          })
        );
      })
    );
  });
  //End of BiaIntroEffects

  constructor(
    private actions$: Actions,
    private impactService: ImpactService,
    private store: Store<Bia>
  ) {}
}
